import React from "react"
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import PaymentTypeIcon from "../invoice/paymenttypeicon.component"
import { OnlinePaymentReceipt } from "../state/helpers/OnlinePaymentReceipt"
import { PAYMENT_STATUS } from "../state/helpers/Payment"
import ProposalMergeFields from "../template/MergeFields"
import BankAccountDetails from "./bankaccountdetails.component"
import CreditCardDetails from "./creditcarddetails.component"
import ExternalCheckoutDetails from "./ExternalCheckoutDetailsComponent"
import PaymentStatus from "./status.component"

type OwnProps = {
    pay: OnlinePaymentReceipt,
    variables,
}

const PaymentDetails = (props: OwnProps) => {
    const { pay, variables } = props

    const getTerminalName = () => {
        if (!pay.terminalId) {
            return "-"
        }
        return variables.terminalMapping[pay.terminalId] ?? pay.terminalId
    }

    const getUserName = () => {
        if (!pay.terminalId) {
            return "-"
        }
        return variables.userMapping[pay.createdByUserId] ?? pay.createdByUserId
    }

    const getDeclineReason = () => {
        if (pay.notes) {
            return pay.notes
        } else if (!pay?.creditCardDetails?.attributes?.last4) {
            return <FormattedMessage
                id="payment.decline_reason.cancelled"
                defaultMessage="Payment was cancelled."
            />
        }
        return "-"
    }

    const getAdditionalInfo = () => {
        switch (pay.status) {
        case PAYMENT_STATUS.DECLINED:
            const declineReason = getDeclineReason()
            return (
                <>
                    <p className="mb-1 border-bottom">
                        <small>
                            <FormattedMessage
                                id="payment.decline_reason"
                                defaultMessage="Decline Reason"
                            />
                        </small>
                    </p>
                    <p className={declineReason === "-" ? "mb-0" : "mb-0 text-danger text-wrap"}>
                        {declineReason}
                    </p>
                </>
            )
        case PAYMENT_STATUS.PAID:
            return (
                <>
                    <p className="mb-1 border-bottom">
                        <small>
                            <FormattedMessage
                                id="payment.authorization_code"
                                defaultMessage="Authorization code"
                            />
                        </small>
                    </p>
                    <p className="mb-0">
                        {pay.authorizationCode || "-"}
                    </p>
                </>
            )
        case PAYMENT_STATUS.PENDING:
            return pay.creditCardDetails ? (
                <>
                    <p className="mb-1 border-bottom">
                        <small>
                            <FormattedMessage
                                id="payment.pending_reason"
                                defaultMessage="Pending Reason"
                            />
                        </small>
                    </p>
                    <FormattedMessage
                        id="payment.pending_warning"
                        defaultMessage={"We are waiting for the payment confirmation. " +
                            "If you didn't complete the payment previously, please try again in 5 minutes."}
                    />
                </>
            ) : (<></>)
        default:
            return <></>
        }
    }

    return (
        <div key={pay.id} className="row d-flex justify-content-flex-start mb-2">
            <div className="d-flex justify-content-flex-start flex-grow-1 bg-light p-2">
                <div className="my-auto px-3" style={{ fontSize: "2em", width: "8%" }}>
                    <PaymentTypeIcon
                        className={pay.isDeclined ? "text-danger" : ""}
                        value={pay.paymentType}
                        icon={pay.icon}
                    />
                </div>
                <div className="px-3" style={{ width: "23%" }}>
                    <p className="mb-1 border-bottom">
                        {
                            pay.isDeposit ?
                                <small>
                                    <FormattedMessage
                                        id="payment.deposit_received_on"
                                        defaultMessage="Deposit received on"
                                    />
                                </small> :
                                <small>
                                    <FormattedMessage
                                        id="payment.payment_received_on"
                                        defaultMessage="Payment received on"
                                    />
                                </small>
                        }
                    </p>
                    <p className="mb-2">
                        <FormattedDate
                            year='numeric'
                            month='long'
                            day='numeric'
                            value={new Date(pay.createdAt)}
                            timeZone={ProposalMergeFields.getTimeZone(variables)}
                        />
                    </p>
                    <p className="mb-1 border-bottom">
                        <small>
                            <FormattedMessage
                                id="payment.payment_amount"
                                defaultMessage="Payment amount"
                            />
                        </small>
                    </p>
                    <p className="mb-0">
                        <FormattedNumber
                            style={"currency"}
                            currency={pay.currency}
                            value={pay.amount}
                        />
                    </p>
                </div>
                {
                    pay.creditCardDetails && <CreditCardDetails pay={pay} />
                }
                {
                    pay.bankDetails &&  <BankAccountDetails pay={pay} />
                }
                {
                    !pay.creditCardDetails && pay.externalCheckoutDetails && <ExternalCheckoutDetails pay={pay} variables={variables} />
                }
                {pay.creditCardDetails && (
                    <div className="px-3" style={{ width: "23%" }}>
                        <p className="mb-1 border-bottom">
                            <small>
                                <FormattedMessage
                                    id="payment.terminal_id"
                                    defaultMessage="Terminal ID"
                                />
                            </small>
                        </p>
                        <p className="mb-2">
                            {getTerminalName()}
                        </p>
                        <p className="mb-1 border-bottom">
                            <small>
                                <FormattedMessage
                                    id="payment.employee_id"
                                    defaultMessage="Employee name/ID"
                                />
                            </small>
                        </p>
                        <p className="mb-0">
                            {getUserName()}
                        </p>
                    </div>
                )}
                <div className="px-3" style={{ width: "23%" }}>
                    {getAdditionalInfo()}
                    {pay.applicationLabel && <>
                        <p className="mb-1 border-bottom">
                            <small>
                                <FormattedMessage
                                    id="payment.application_label"
                                    defaultMessage="Application label"
                                />
                            </small>
                        </p>
                        <p className="mb-2">
                            {pay.applicationLabel}
                        </p>
                    </>
                    }
                </div>
                <div className="px-3" style={{ maxWidth: "46%" }}>
                    <p className="mb-1 border-bottom">
                        <small>
                            <FormattedMessage
                                id="payment.status"
                                defaultMessage="Status"
                            />
                        </small>
                    </p>
                    <p className="mb-2">
                        <pre className="mb-0">
                            <PaymentStatus value={pay.status} />
                        </pre>
                    </p>
                </div>
            </div>
        </div>
    )
}

export default injectIntl(PaymentDetails)


// WEBPACK FOOTER //
// ./src/payment/paymentdetails.component.tsx